import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Camera, Search } from 'lucide-react';
import { SkeletonList } from '../components/ui/Skeleton';
import { PageShell } from '../components/ui/PageShell';
import { SectionGroup } from '../components/ui/SectionGroup';
import { Room, Project, Photo } from '../types';
import { getProject } from '../services/projectService';
import { getRoom, createRoom, updateRoom, deleteRoom } from '../services/roomService';
import { getPhotosByRoom } from '../services/photoService';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { useFormNavigation } from '../hooks/useFormNavigation';
import UnsavedChangesDialog from '../components/UnsavedChangesDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import PhotoSelector from '../components/PhotoSelector';
import CameraComponent from '../components/CameraComponent';
import ROOM_TYPES from '../utils/roomTypes';
import { useFullScreen } from '../components/Layout';
import { useNotifications } from '../contexts/NotificationContext';

const RoomFormPage: React.FC = () => {
  const { projectId, roomId } = useParams<{ projectId: string; roomId: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  const isOnline = useOnlineStatus();
  const isEditMode = !!roomId;

  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formChanged, setFormChanged] = useState(false);
  const [initialFormData, setInitialFormData] = useState<Partial<Room> | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [showPhotoSelector, setShowPhotoSelector] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCamera, setShowCamera] = useState(false);

  const { setIsFullScreen } = useFullScreen();

  const [formData, setFormData] = useState<Partial<Room>>({
    projectId: projectId || '',
    name: '',
    notes: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhotoSelectedFromLibrary = async (selectedPhoto: Photo) => {
    if (!projectId) return;

    if (isEditMode && roomId) {
      try {
        const { updateDoc, doc } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        const { serverTimestamp } = await import('firebase/firestore');

        const photoRef = doc(db, 'photos', selectedPhoto.id);
        await updateDoc(photoRef, {
          roomId: roomId,
          updatedAt: serverTimestamp()
        });

        const updatedPhoto = { ...selectedPhoto, roomId: roomId };
        setPhotos(prev => [...prev, updatedPhoto]);

        addNotification({ type: 'success', message: 'Foto kopplat till rummet', duration: 2000 });

      } catch (err) {
        console.error('Error associating photo with room:', err);
        setError('Kunde inte koppla fotot till rummet. Försök igen senare.');
        setTimeout(() => setError(null), 3000);
      }
    } else {
      setPhotos(prev => [...prev, selectedPhoto]);
      addNotification({ type: 'success', message: 'Foto valt för rummet', duration: 2000 });
    }
  };

  const handleSaveAndNavigate = async (): Promise<boolean> => {
    if (!projectId) return false;

    if (!isOnline) {
      setError('Du är offline. Anslut till internet för att spara rummet.');
      return false;
    }

    try {
      setSaving(true);

      if (isEditMode && roomId) {
        await updateRoom(roomId, formData);
        setInitialFormData(formData);
        setFormChanged(false);
        addNotification({ type: 'success', message: 'Rummet har sparats', duration: 2000 });
        navigate(`/projects/${projectId}/rooms/${roomId}`);
        return true;
      } else {
        if (!formData.name) {
          setError('Rumstyp måste anges');
          return false;
        }

        const roomData = {
          projectId,
          name: formData.name,
          notes: formData.notes || ''
        } as Omit<Room, 'id' | 'createdAt' | 'updatedAt'>;

        const result = await createRoom(roomData);
        const newRoomId = result.id;

        // If there are selected photos, associate them with the new room
        if (photos.length > 0) {
          try {
            const { updateDoc, doc } = await import('firebase/firestore');
            const { db } = await import('../firebase');
            const { serverTimestamp } = await import('firebase/firestore');

            const updatePromises = photos.map(photo => {
              const photoRef = doc(db, 'photos', photo.id);
              return updateDoc(photoRef, {
                roomId: newRoomId,
                updatedAt: serverTimestamp()
              });
            });

            await Promise.all(updatePromises);
          } catch (photoErr) {
            console.error('Error associating photos with new room:', photoErr);
          }
        }

        addNotification({ type: 'success', message: `${formData.name} har skapats`, duration: 2000 });
        navigate(`/projects/${projectId}/rooms`);
        return true;
      }
    } catch (err) {
      console.error('Error saving room:', err);
      setError('Kunde inte spara rummet. Försök igen senare.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Form navigation safety
  const {
    navigateSafely,
    showDialog,
    handleSave,
    handleDiscard,
    handleContinueEditing,
  } = useFormNavigation({
    onSave: handleSaveAndNavigate,
    hasUnsavedChanges: formChanged
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!projectId) return;

      try {
        setLoading(true);

        const [fetchedProject, fetchedRoom, roomPhotos] = await Promise.all([
          getProject(projectId),
          isEditMode && roomId ? getRoom(roomId) : Promise.resolve(null),
          isEditMode && roomId ? getPhotosByRoom(projectId, roomId).catch(() => []) : Promise.resolve([]),
        ]);

        setProject(fetchedProject);

        if (isEditMode && fetchedRoom) {
          setFormData(fetchedRoom);
          setInitialFormData(fetchedRoom);
          setPhotos(roomPhotos);
        } else {
          setInitialFormData(formData);
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Kunde inte hämta data. Försök igen senare.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId, roomId, isEditMode]);

  // Hide tab bar when camera is open
  useEffect(() => {
    setIsFullScreen(showCamera);
    return () => setIsFullScreen(false);
  }, [showCamera, setIsFullScreen]);

  // Track form changes
  useEffect(() => {
    if (loading || !initialFormData) return;
    const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialFormData);
    setFormChanged(hasChanges);
  }, [formData, initialFormData, loading]);

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    await handleSaveAndNavigate();
  };

  const refreshPhotos = async () => {
    if (!projectId || !roomId) return;
    try {
      setPhotos(await getPhotosByRoom(projectId, roomId));
    } catch (err) {
      console.error('Error refreshing photos:', err);
    }
  };

  // Fires per saved photo while the camera stays open for the next shot.
  const handlePhotoTaken = (photoId: string) => {
    refreshPhotos();
  };

  const handleCameraClose = () => {
    setShowCamera(false);
    refreshPhotos();
  };

  const handleDelete = () => {
    if (!roomId || !isOnline) return;
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!roomId || !projectId) return;
    setShowDeleteConfirm(false);
    try {
      setSaving(true);
      await deleteRoom(roomId, projectId);
      navigate(`/projects/${projectId}/rooms`);
    } catch (err) {
      console.error('Error deleting room:', err);
      setError('Kunde inte ta bort rummet. Försök igen senare.');
    } finally {
      setSaving(false);
    }
  };

  const backLink = `/projects/${projectId}/rooms`;

  if (loading) {
    return (
      <PageShell title={isEditMode ? 'Redigera rum' : 'Nytt rum'} backTo={backLink} backLabel="Rum">
        <div className="space-y-4 py-2">
          <SkeletonList count={3} />
        </div>
      </PageShell>
    );
  }

  if (!project) {
    return (
      <PageShell title="Rum" backTo="/" backLabel="Tillbaka">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl text-sm mt-4">
          Projektet kunde inte hittas.
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      title={isEditMode ? 'Redigera rum' : 'Nytt rum'}
      onBack={() => navigateSafely(backLink)}
      backLabel="Rum"
      headerRight={
        <div className="flex items-center gap-4">
          {isEditMode && (
            <button
              type="button"
              onClick={handleDelete}
              className="text-red-500 active:text-red-700 text-[17px] font-normal transition-colors"
            >
              Radera
            </button>
          )}
          <button
            type="button"
            onClick={() => handleSubmit()}
            disabled={saving}
            className={`text-[17px] font-semibold transition-colors ${
              saving
                ? 'text-[var(--text-tertiary)]'
                : 'text-accent-600 dark:text-accent-400 active:opacity-50'
            }`}
          >
            {saving ? 'Sparar…' : 'Spara'}
          </button>
        </div>
      }
    >
      <UnsavedChangesDialog
        isOpen={showDialog}
        onClose={handleContinueEditing}
        onSave={handleSave}
        onDiscard={handleDiscard}
      />

      <form onSubmit={(e) => e.preventDefault()} className="space-y-6 pb-8">
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border-l-2 border-red-400 px-3 py-2.5 rounded-ios-card">
            <p className="text-[13px] text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Project Info */}
        <SectionGroup title="Projekt">
          <div className="px-4 py-3">
            <p className="text-[15px] text-[var(--text-secondary)]">
              {project.address.street}, {project.address.city}
            </p>
          </div>
        </SectionGroup>

        {/* Room Details */}
        <SectionGroup title="Rumsdetaljer">
          <div className={`flex items-center gap-3 px-4 py-3 min-h-[44px] inset-divider`}>
            <label className="text-[15px] text-[var(--text-primary)] w-24 sm:w-28 flex-shrink-0">
              Rumstyp<span className="text-red-500 ml-0.5">*</span>
            </label>
            <div className="flex-1 min-w-0">
              <select
                name="name"
                value={formData.name || ''}
                onChange={handleInputChange}
                required
                className="bg-transparent text-[15px] text-[var(--text-primary)] w-full outline-none py-0 appearance-none cursor-pointer text-right"
              >
                <option value="" disabled>Välj rumstyp</option>
                {ROOM_TYPES.map((roomType, index) => (
                  <option key={index} value={roomType}>
                    {roomType}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="px-4 py-3">
            <label className="text-[15px] text-[var(--text-primary)] mb-1.5 block">Anteckningar</label>
            <textarea
              name="notes"
              value={formData.notes || ''}
              onChange={handleInputChange}
              rows={4}
              placeholder="Anteckningar om rummet..."
              className="w-full bg-transparent text-[15px] text-[var(--text-primary)] placeholder-[var(--text-tertiary)] outline-none resize-none"
            />
          </div>
        </SectionGroup>

        {/* Photos */}
        <SectionGroup title="Foton">
          <div className="px-4 py-3 space-y-3">
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setShowCamera(true)}
                className="flex items-center text-accent-600 text-[15px] active:opacity-50"
              >
                <Camera size={16} className="mr-1" />
                Ta foto
              </button>
              <button
                type="button"
                onClick={() => setShowPhotoSelector(true)}
                className="flex items-center text-accent-600 text-[15px] active:opacity-50"
              >
                <Search size={16} className="mr-1" />
                Välj från bibliotek
              </button>
            </div>

            {photos.length > 0 ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {photos.map((photo) => (
                  <div key={photo.id} className="relative aspect-square rounded-lg overflow-hidden">
                    <img
                      src={photo.thumbnail || photo.url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-[var(--text-tertiary)] text-[13px]">
                <Camera size={20} className="mx-auto mb-1.5 opacity-40" />
                <p>Inga foton kopplade ännu</p>
              </div>
            )}
          </div>
        </SectionGroup>
      </form>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Ta bort"
        message="Är du säker på att du vill ta bort detta rum? Detta kan inte ångras."
        confirmLabel="Ta bort"
        cancelLabel="Avbryt"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      {showPhotoSelector && (
        <PhotoSelector
          projectId={projectId || ''}
          excludePhotoIds={photos.map(p => p.id)}
          onPhotoSelected={handlePhotoSelectedFromLibrary}
          onClose={() => setShowPhotoSelector(false)}
        />
      )}

      {showCamera && (
        <CameraComponent
          projectId={projectId || ''}
          roomId={roomId}
          onPhotoTaken={handlePhotoTaken}
          onClose={handleCameraClose}
        />
      )}
    </PageShell>
  );
};

export default RoomFormPage;
